import { useState, useEffect, useRef } from "react";
import './Common.css';

export default function MultipleSearchSelectApi({
  fetchApi,
  values = undefined, // allow either prop
  value = undefined,  // alias from parent
  onChange,
  onOk
}) {
  // use either values or value (support both prop names)
  const selectedProp = values ?? value ?? [];

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState([]);

  const wrapperRef = useRef(null);

  // click outside
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // debounce fetch
  useEffect(() => {
    let active = true;
    const tm = setTimeout(async () => {
      try {
        const res = await fetchApi(search);
        if (active) setOptions(res || []);
      } catch {
        if (active) setOptions([]);
      }
    }, 300);
    return () => {
      active = false;
      clearTimeout(tm);
    };
  }, [search, fetchApi]);

  const toggleSelect = (opt) => {
    const exists = selectedProp.some((v) => v.value === opt.value);
    if (exists) {
      onChange?.(selectedProp.filter((v) => v.value !== opt.value));
    } else {
      onChange?.([...selectedProp, opt]);
    }
  };

  const removeTag = (val) => {
    onChange?.(selectedProp.filter((v) => v.value !== val));
  };

  const isSelected = (id) => selectedProp.some((v) => v.value === id);

  // CHECK ALL (only tick current dropdown items)
  const handleCheckAll = () => {
    const notSelected = options.filter(o => !isSelected(o.value));
    onChange?.([...selectedProp, ...notSelected]);
  };

  return (
    <div className="position-relative ms-select" ref={wrapperRef}>
      {/* tags */}
      <div
        className="form-control d-flex flex-wrap gap-2"
        style={{ minHeight: "42px", cursor: "text" }}
        onClick={() => setOpen(true)}
      >
        {selectedProp.map((item) => (
          <span
            key={item.value}
            className="badge bg-primary d-flex align-items-center"
          >
            {item.label}
            <span
              className="ms-2"
              style={{ cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();
                removeTag(item.value);
              }}
            >
              ×
            </span>
          </span>
        ))}

        <input
          type="text"
          className="border-0 flex-grow-1"
          style={{ outline: "none" }}
          placeholder="Search..."
          value={search}
          onFocus={() => setOpen(true)}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* dropdown */}
      {open && (
        <div
          className="border rounded bg-white position-absolute w-100 mt-1 shadow-sm"
          style={{ maxHeight: "260px", overflowY: "auto", zIndex: 1000 }}
        >
          {/* top bar: count + check all + ok */}
          <div className="d-flex justify-content-between align-items-center p-2 border-bottom bg-light">
            <span className="small text-muted">
              Selected: {selectedProp.length}
            </span>

            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-outline-primary"
                type="button"
                onClick={handleCheckAll}
              >
                Check all
              </button>

              <button
                className="btn btn-sm btn-primary"
                type="button"
                onClick={() => {
                  // pass current selection back to parent so it can apply filters immediately
                  onOk?.(selectedProp);
                  setOpen(false);
                }}
              >
                OK
              </button>
            </div>
          </div>

          {/* items */}
          {options.map((opt) => (
            <div
              key={opt.value}
              className="p-2 hover-bg d-flex align-items-center gap-2"
              style={{ cursor: "pointer" }}
              onClick={() => toggleSelect(opt)}
            >
              <input
                type="checkbox"
                checked={isSelected(opt.value)}
                readOnly
                style={{ pointerEvents: "none" }}
              />
              <span>{opt.label}</span>
            </div>
          ))}

          {options.length === 0 && (
            <div className="p-2 text-muted small">No results</div>
          )}
        </div>
      )}
    </div>
  );
}
